/*!
 * TinyPoker JP Banker 試算頁
 *
 * 老闆＝Bad Beat Jackpot 的莊家（Banker）。這頁純試算他在 JP 上的損益：
 *   實收   = 真人 JP fee    + 自營Bot JP fee    + Eco JP fee    × Share%
 *   實付   = 真人 JP payout + 自營Bot JP payout + Eco JP payout × Share%
 *   淨損益 = 實收 − 實付
 *
 * 🔑 自營Bot 對 Banker 視同真人（v2.51.1，Brad 2026-10-04 定案）：它的 JP 是兩個口袋
 *    之間的移轉 —— fee 由聯盟付給 Banker、payout 由 Banker 付給聯盟（營收「自營Bot 損益」）。
 *    所以全額計入、沒有比例可調，卡片底下列出聯盟那邊的對應數字。
 *    🚫 不要加回「自營Bot Share%」（v2.51.0 做過又拿掉）：營收沒跟著改時，調低它會讓帳上憑空少錢。
 *
 * Eco 的 Share% 可逐週 / 批次調整並存回 DB（獨立 collection，純試算、零副作用）。
 * 幣別一律 TWD。200 萬初始投入 / 獎池現值這版不入帳（之後接即時爬蟲再處理）。
 *
 * 「近 N 週」篩選是**純前端的顯示範圍**，不重抓資料：
 *   - 摘要卡、組成明細、Eco 原始、表格合計 全部只算畫面上那幾週
 *   - 批次調整只作用在**畫面上**勾選的週（藏起來的勾選不會被改到）
 *   - 🔑 儲存仍然送**全部週**的 Share —— 篩選只是看，不是只存這幾週
 *   - 獎池現值是即時快照、不是逐週加總，不受篩選影響
 *
 * 視覺風格參照 TinyClubConfig.js（dark theme）。
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Table,
  Input,
  Alert,
  Badge,
} from "reactstrap";
import axios from "axios";
import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyLoading from "components/tiny/TinyLoading";
import { WEEK_CTRL_HEIGHT } from "components/tiny/TinyWeekWindowNav";
import { COLORS as THEME } from "constant/adminTheme";

const API_BASE = "/api/tiny";

// ==================== 共用樣式常量（沿用 TinyClubConfig 風格）====================
const COLORS = {
  cardBg: "#1e2530",
  headerBg: "#252d3a",
  inputBg: "#1a1f2b",
  border: "rgba(255,255,255,0.1)",
  borderInput: "rgba(255,255,255,0.2)",
  textPrimary: "#fff",
  textSecondary: "rgba(255,255,255,0.8)",
  textMuted: "rgba(255,255,255,0.5)",
  accent: "#667eea",
  accentGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  success: "#2dce89",
  danger: "#f5365c",
  info: "#11cdef",
  warning: "#fb6340",
  eco: "#11cdef",
  selfBot: "#00d6b4", // 與自營Bot 數據頁、營收頁同色
};

const thStyle = {
  borderColor: COLORS.border,
  padding: "11px 10px",
  fontWeight: 600,
  fontSize: "12px",
  whiteSpace: "nowrap",
  color: COLORS.textSecondary,
};
// 兩層表頭的上層（分組名）與下層（Fee / Payout / Share）。
// 主題預設把 th 轉大寫，分組名要保留「自營Bot」原樣所以關掉。
const groupTh = (color) => ({
  ...thStyle,
  textAlign: "center",
  color,
  textTransform: "none",
  padding: "8px 6px 4px",
  borderBottom: `2px solid ${color === COLORS.textPrimary ? COLORS.border : color + "66"}`,
});
const subTh = (align, color) => ({
  ...thStyle,
  textAlign: align,
  color: color || COLORS.textSecondary,
  padding: "5px 6px 8px",
  fontSize: "11.5px",
});

const tdStyle = {
  borderColor: "rgba(255,255,255,0.05)",
  padding: "9px 6px",
  verticalAlign: "middle",
  fontSize: "13px",
  fontVariantNumeric: "tabular-nums",
};

const fmt = (n) =>
  (n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const p2 = (n) => String(n).padStart(2, "0");
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

// fraction(0.3) <-> percent(30)
const toPct = (f) => Math.round((f || 0) * 1000) / 10;
const toFrac = (p) => (isNaN(parseFloat(p)) ? 0 : parseFloat(p) / 100);

const netColor = (v) => (v >= 0 ? COLORS.success : COLORS.danger);

// 分帳比例配色：30% → 藍、50% → 橘、其他自訂 → 紫，一眼分辨
const shareTheme = (pct) => {
  if (pct >= 50)
    return { color: "#fb6340", bg: "rgba(251,99,64,0.15)", border: "rgba(251,99,64,0.5)" };
  if (pct >= 40)
    return { color: "#a884ff", bg: "rgba(118,75,162,0.18)", border: "rgba(150,100,220,0.5)" };
  return { color: "#11cdef", bg: "rgba(17,205,239,0.13)", border: "rgba(17,205,239,0.5)" };
};

// 「近 N 週」快捷選項（13 ≈ 一季、26 ≈ 半年）；其他數字用自訂輸入
const RECENT_PRESETS = [4, 8, 13, 26];

// 週期字串 "20260803-20260809" 的起訖 → "2026/08/03 – 09/27"（跨年才兩邊都帶年份）
const fmtWeekSpan = (from, to) => {
  const ymd = (s) => `${s.slice(0, 4)}/${s.slice(4, 6)}/${s.slice(6, 8)}`;
  return from.slice(0, 4) === to.slice(0, 4)
    ? `${ymd(from)} – ${to.slice(4, 6)}/${to.slice(6, 8)}`
    : `${ymd(from)} – ${ymd(to)}`;
};

function TinyJPBanker() {
  // [{week, real_fee, real_payout, eco_fee, eco_payout, share, self_fee, self_payout}]
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const [selected, setSelected] = useState(() => new Set());
  const [customPct, setCustomPct] = useState("");
  const [sortDir, setSortDir] = useState("desc"); // 預設最新週在最上面
  const [recentN, setRecentN] = useState(null); // 只看最新 N 週；null ＝ 全部

  // ---------- 載入 ----------
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/jp-banker/data`);
      if (res.data.code === 1) {
        setRows(res.data.data.weeks || []);
        setMeta(res.data.data);
      } else {
        setError(res.data.msg || "載入失敗");
      }
    } catch (err) {
      setError("載入失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---------- 逐週改 Share ----------
  const setShare = (week, pct) => {
    const frac = toFrac(pct);
    setRows((prev) => prev.map((r) => (r.week === week ? { ...r, share: frac } : r)));
  };

  const stepShare = (week, delta) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.week !== week) return r;
        const pct = Math.max(0, Math.min(100, Math.round(toPct(r.share)) + delta));
        return { ...r, share: pct / 100 };
      })
    );
  };

  const stepCustom = (delta) => {
    const pct = Math.max(0, Math.min(100, Math.round(parseFloat(customPct) || 0) + delta));
    setCustomPct(String(pct));
  };

  // ---------- 勾選 ----------
  const toggleOne = (week) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(week) ? next.delete(week) : next.add(week);
      return next;
    });
  };

  // ---------- 儲存 ----------
  const save = async () => {
    setSaving(true);
    setSaveMsg(null);
    setError(null);
    try {
      // 送全部週（包含被「近 N 週」藏起來的），篩選只影響畫面
      const shares = {};
      rows.forEach((r) => {
        shares[r.week] = r.share;
      });
      const res = await axios.put(`${API_BASE}/jp-banker/shares`, { shares });
      if (res.data.code === 1) {
        setSaveMsg("分帳比例已儲存");
        setTimeout(() => setSaveMsg(null), 3000);
      } else {
        setError(res.data.msg || "儲存失敗");
      }
    } catch (err) {
      setError("儲存失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ---------- 計算（隨 Share 即時連動）+ 近 N 週篩選 + 排序 ----------
  const computed = useMemo(() => {
    const newestFirst = rows
      .map((r) => {
        // 自營Bot 視同真人：全額計入
        const recv = r.real_fee + r.self_fee + r.eco_fee * r.share;
        const paid = r.real_payout + r.self_payout + r.eco_payout * r.share;
        // 這週自營Bot 有沒有 JP（沒有的週表格顯示「—」，那時自營Bot 還不存在）
        const hasSelf = r.self_fee !== 0 || r.self_payout !== 0;
        return { ...r, recv, paid, net: recv - paid, hasSelf };
      })
      .sort((a, b) => b.week.localeCompare(a.week));
    // 「近 N 週」＝資料裡最新的 N 週，跟表格排序方向無關
    const out = recentN ? newestFirst.slice(0, recentN) : newestFirst;
    const totals = out.reduce(
      (a, r) => {
        a.real_fee += r.real_fee;
        a.real_payout += r.real_payout;
        a.eco_fee += r.eco_fee;
        a.eco_payout += r.eco_payout;
        a.eco_fee_share += r.eco_fee * r.share;
        a.eco_payout_share += r.eco_payout * r.share;
        a.self_fee += r.self_fee;
        a.self_payout += r.self_payout;
        a.recv += r.recv;
        a.paid += r.paid;
        a.net += r.net;
        return a;
      },
      {
        real_fee: 0,
        real_payout: 0,
        eco_fee: 0,
        eco_payout: 0,
        eco_fee_share: 0,
        eco_payout_share: 0,
        self_fee: 0,
        self_payout: 0,
        recv: 0,
        paid: 0,
        net: 0,
      }
    );
    const sorted = sortDir === "asc" ? [...out].reverse() : out;
    const span = out.length
      ? fmtWeekSpan(out[out.length - 1].week.slice(0, 8), out[0].week.slice(9, 17))
      : "";
    return { sorted, totals, count: out.length, span };
  }, [rows, sortDir, recentN]);

  // 有沒有真的藏掉週（N 大於等於總週數時等於全部）
  const filtered = computed.count < rows.length;
  // 摘要卡標題的前綴：「累計實收」→「近 8 週實收」，篩選中用導覽色提醒數字不是全部
  const scopeLabel = filtered ? (
    <span style={{ color: THEME.navActive, fontWeight: 600 }}>近 {computed.count} 週</span>
  ) : (
    "累計"
  );

  // ---------- 勾選 / 批次：只認畫面上看得到的週 ----------
  // 被篩選藏起來的勾選保留在 state（放寬範圍時還在），但不計數、不會被批次改到
  const shownWeeks = computed.sorted.map((r) => r.week);
  const shownSelected = shownWeeks.filter((w) => selected.has(w));
  const nSel = shownSelected.length;
  const allSelected = shownWeeks.length > 0 && nSel === shownWeeks.length;
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      shownWeeks.forEach((w) => (allSelected ? next.delete(w) : next.add(w)));
      return next;
    });
  };
  const applyToSelected = (frac) => {
    if (nSel === 0) return;
    const target = new Set(shownSelected);
    setRows((prev) => prev.map((r) => (target.has(r.week) ? { ...r, share: frac } : r)));
  };

  if (loading) {
    return (
      <div className="content">
        <TinyLoading text="整理每週 JP 資料" />
      </div>
    );
  }

  const card = { background: COLORS.cardBg, border: `1px solid ${COLORS.border}`, marginBottom: 20 };

  return (
    <div className="content">
      {/* 隱藏 number input 的原生上下箭頭（spinner），讓輸入框乾淨 */}
      <style>{`
        .jpb-num::-webkit-outer-spin-button,
        .jpb-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .jpb-num { -moz-appearance: textfield; appearance: textfield; }
        .jpb-step { background: transparent; border: none; padding: 0; margin: 0;
          height: 10px; width: 16px; line-height: 10px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          opacity: 0.55; transition: opacity .12s; font-size: 11px; }
        .jpb-step:hover { opacity: 1; }
        .jpb-step:focus { outline: none; }
      `}</style>
      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saveMsg && <Alert color="success">{saveMsg}</Alert>}

      {/* 圖示不用傳 —— 公版會自動用 routes.js 裡側欄那一顆（fa-calculator），
          標題與側欄因此不可能不一致。範圍控制是「近 N 週」（純前端篩選、
          不重抓資料），沒有子視圖。 */}
      <TinyPageHeader
        title="JP Banker 試算"
        badges={[{ text: "純試算 · TWD", tone: "accent" }]}
        description="老闆＝Bad Beat Jackpot 莊家。調整每週 Eco 分帳比例（Share%），即時試算 Banker 實收 / 實付 / 損益。真人與自營Bot 全額計入（自營Bot 視同真人），Eco 依 Share% 折算。此頁不影響任何既有交收機制。"
        scope={
          <RecentWeeksFilter
            value={recentN}
            onChange={setRecentN}
            span={computed.span}
            count={computed.count}
          />
        }
      />

      {/* Summary 卡 */}
      <Row>
        <Col md="3" sm="6">
          <SummaryCard title={<>{scopeLabel}實收</>} value={computed.totals.recv} sub="真人 / 自營Bot fee + Eco fee × Share" />
        </Col>
        <Col md="3" sm="6">
          <SummaryCard title={<>{scopeLabel}實付</>} value={computed.totals.paid} sub="真人 / 自營Bot payout + Eco payout × Share" />
        </Col>
        <Col md="3" sm="6">
          <Card style={{ ...card, borderColor: netColor(computed.totals.net) + "66", marginBottom: 12 }}>
            <CardBody style={{ textAlign: "center", padding: "11px 14px" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 11.5, marginBottom: 3 }}>{scopeLabel}淨損益</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: netColor(computed.totals.net) }}>
                {computed.totals.net >= 0 ? "+" : ""}
                {fmt(computed.totals.net)}
              </div>
              <small style={{ color: COLORS.textMuted, fontSize: 10.5 }}>實收 − 實付（中獎當下）</small>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6">
          <PoolValueCard pool={meta.pool_value} />
        </Col>
      </Row>

      {/* 組成明細：真人 / Eco 拆算後 / 自營Bot —— 各 Fee / Payout / 淨值（Eco 原始在下方灰條） */}
      <div style={{ color: COLORS.textSecondary, fontSize: 12.5, fontWeight: 600, margin: "0 4px 7px" }}>
        組成明細
        <span style={{ color: COLORS.textMuted, fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
          淨值 = Fee − Payout（Banker 角度）
        </span>
      </div>
      <Row>
        <Col md="4">
          <GroupCard
            title="真人"
            subtitle="全額計入"
            color="#5b8def"
            fee={computed.totals.real_fee}
            payout={computed.totals.real_payout}
          />
        </Col>
        <Col md="4">
          <GroupCard
            title="Eco 拆算後"
            subtitle="× Share，隨比例連動"
            color={COLORS.warning}
            live
            fee={computed.totals.eco_fee_share}
            payout={computed.totals.eco_payout_share}
          />
        </Col>
        <Col md="4">
          <GroupCard
            title="自營Bot"
            subtitle="視同真人，全額計入"
            color={COLORS.selfBot}
            fee={computed.totals.self_fee}
            payout={computed.totals.self_payout}
            footer={<UnionMirrorLine fee={computed.totals.self_fee} payout={computed.totals.self_payout} />}
          />
        </Col>
      </Row>
      <div style={{ color: COLORS.textMuted, fontSize: 11, margin: "-4px 4px 8px" }}>
        {scopeLabel}淨損益 = <b style={{ color: "#5b8def" }}>真人淨值</b>{" "}
        <span style={{ color: COLORS.textSecondary }}>+</span>{" "}
        <b style={{ color: COLORS.warning }}>Eco 拆算後淨值</b>{" "}
        <span style={{ color: COLORS.textSecondary }}>+</span>{" "}
        <b style={{ color: COLORS.selfBot }}>自營Bot 淨值</b>
      </div>

      {/* Eco 原始：次要參考（未套 Share），灰色 slim strip */}
      <SecondaryStrip fee={computed.totals.eco_fee} payout={computed.totals.eco_payout} />

      {/* 批次操作列 */}
      <Card style={card}>
        <CardBody
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            padding: "14px 18px",
          }}
        >
          <span style={{ fontWeight: 600, color: COLORS.textSecondary, fontSize: 13 }}>
            批次調整（已選 {nSel} 週）：
          </span>
          <Button
            size="sm"
            disabled={nSel === 0}
            onClick={() => applyToSelected(0.3)}
            style={pillBtn(shareTheme(30), nSel === 0)}
          >
            設 30%
          </Button>
          <Button
            size="sm"
            disabled={nSel === 0}
            onClick={() => applyToSelected(0.5)}
            style={pillBtn(shareTheme(50), nSel === 0)}
          >
            設 50%
          </Button>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                ...miniInput,
                width: 96,
                display: "inline-flex",
                alignItems: "center",
                paddingRight: 4,
              }}
            >
              <Input
                type="number"
                className="jpb-num"
                bsSize="sm"
                placeholder="自訂 %"
                min={0}
                max={100}
                step={1}
                style={{
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                  color: COLORS.textPrimary,
                  width: 60,
                  padding: "4px 6px",
                }}
                value={customPct}
                onChange={(e) => setCustomPct(e.target.value)}
              />
              <span style={{ color: COLORS.textMuted, fontSize: 12, marginRight: 2 }}>%</span>
              <Stepper color={COLORS.textSecondary} onUp={() => stepCustom(5)} onDown={() => stepCustom(-5)} />
            </div>
            <Button
              size="sm"
              disabled={nSel === 0 || customPct === ""}
              onClick={() => applyToSelected(toFrac(customPct))}
              style={pillBtn(shareTheme(parseFloat(customPct) || 0), nSel === 0 || customPct === "")}
            >
              套用自訂
            </Button>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Button
              size="sm"
              onClick={load}
              disabled={saving}
              style={{
                background: "rgba(149,165,166,0.15)",
                border: "1px solid rgba(149,165,166,0.4)",
                color: "#95a5a6",
                borderRadius: 6,
              }}
            >
              重設
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              style={{
                background: saving ? "rgba(102,126,234,0.5)" : COLORS.accentGradient,
                border: "none",
                fontWeight: 600,
                borderRadius: 6,
                boxShadow: "0 4px 12px rgba(102,126,234,0.3)",
                padding: "6px 20px",
              }}
            >
              {saving ? "儲存中…" : "儲存分帳比例"}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* 週期明細表 */}
      <Card style={card}>
        <CardBody style={{ padding: "18px 14px" }}>
          <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 14 }}>
            <h5 style={{ color: COLORS.textPrimary, marginBottom: 0, fontWeight: 600 }}>週期明細</h5>
            <small style={{ color: COLORS.textMuted }}>
              預設：{meta.default_cutoff_week_start} 前{" "}
              <span style={{ color: shareTheme(30).color, fontWeight: 600 }}>
                {toPct(meta.default_share_before)}%
              </span>
              、之後{" "}
              <span style={{ color: shareTheme(50).color, fontWeight: 600 }}>
                {toPct(meta.default_share_after)}%
              </span>
            </small>
          </div>
          <div style={{ overflowX: "auto" }}>
            <Table responsive style={{ marginBottom: 0, color: COLORS.textSecondary, whiteSpace: "nowrap" }}>
              {/* 兩層表頭：上層分組（真人 / Eco / 自營Bot / Banker），下層只寫 Fee / Payout / Share。
                  欄位多了自營Bot 三欄之後，單層長標題會把 Banker 三欄擠出畫面。 */}
              <thead style={{ background: COLORS.headerBg }}>
                <tr>
                  <th rowSpan={2} style={{ ...thStyle, width: 34, textAlign: "center", verticalAlign: "middle" }}>
                    <Input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      style={{ position: "static", margin: 0 }}
                    />
                  </th>
                  <th
                    rowSpan={2}
                    style={{ ...thStyle, cursor: "pointer", userSelect: "none", verticalAlign: "middle" }}
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    title="點擊切換週期排序"
                  >
                    週期{" "}
                    <i
                      className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"}`}
                      style={{ color: COLORS.accent, marginLeft: 4 }}
                    />
                  </th>
                  <th colSpan={2} style={groupTh("#5b8def")}>真人</th>
                  <th colSpan={3} style={groupTh(COLORS.eco)}>Eco</th>
                  <th colSpan={2} style={groupTh(COLORS.selfBot)}>自營Bot</th>
                  <th colSpan={3} style={groupTh(COLORS.textPrimary)}>Banker</th>
                </tr>
                <tr>
                  <th style={subTh("right")}>Fee</th>
                  <th style={subTh("right")}>Payout</th>
                  <th style={subTh("right", COLORS.eco)}>Fee</th>
                  <th style={subTh("right", COLORS.eco)}>Payout</th>
                  <th style={subTh("center")}>Share</th>
                  <th style={subTh("right", COLORS.selfBot)}>Fee</th>
                  <th style={subTh("right", COLORS.selfBot)}>Payout</th>
                  <th style={subTh("right")}>實收</th>
                  <th style={subTh("right")}>實付</th>
                  <th style={subTh("right")}>淨損益</th>
                </tr>
              </thead>
              <tbody>
                {computed.sorted.map((r) => {
                  const st = shareTheme(toPct(r.share));
                  const sel = selected.has(r.week);
                  return (
                    <tr
                      key={r.week}
                      style={{
                        borderColor: "rgba(255,255,255,0.05)",
                        background: sel ? "rgba(102,126,234,0.10)" : "transparent",
                      }}
                    >
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <Input
                          type="checkbox"
                          checked={sel}
                          onChange={() => toggleOne(r.week)}
                          style={{ position: "static", margin: 0 }}
                        />
                      </td>
                      <td style={{ ...tdStyle, color: COLORS.textPrimary, fontWeight: 500 }}>{r.week}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{fmt(r.real_fee)}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{fmt(r.real_payout)}</td>
                      <td style={{ ...tdStyle, textAlign: "right", color: COLORS.eco }}>{fmt(r.eco_fee)}</td>
                      <td style={{ ...tdStyle, textAlign: "right", color: COLORS.eco }}>{fmt(r.eco_payout)}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <ShareInput
                          theme={st}
                          value={r.share}
                          onChange={(pct) => setShare(r.week, pct)}
                          onStep={(d) => stepShare(r.week, d)}
                        />
                      </td>
                      {r.hasSelf ? (
                        <>
                          <td style={{ ...tdStyle, textAlign: "right", color: COLORS.selfBot }}>{fmt(r.self_fee)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", color: COLORS.selfBot }}>{fmt(r.self_payout)}</td>
                        </>
                      ) : (
                        <>
                          <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textMuted }}>—</td>
                          <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textMuted }}>—</td>
                        </>
                      )}
                      <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textPrimary }}>
                        {fmt(r.recv)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textPrimary }}>
                        {fmt(r.paid)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: netColor(r.net) }}>
                        {r.net >= 0 ? "+" : ""}
                        {fmt(r.net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, background: COLORS.headerBg }}>
                  <td style={tdStyle}></td>
                  <td style={{ ...tdStyle, color: COLORS.textPrimary }}>合計</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textPrimary }}>{fmt(computed.totals.real_fee)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textPrimary }}>{fmt(computed.totals.real_payout)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: COLORS.eco }}>{fmt(computed.totals.eco_fee)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: COLORS.eco }}>{fmt(computed.totals.eco_payout)}</td>
                  <td style={{ ...tdStyle, textAlign: "center", color: COLORS.textMuted }}>—</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: COLORS.selfBot }}>{fmt(computed.totals.self_fee)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: COLORS.selfBot }}>{fmt(computed.totals.self_payout)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textPrimary }}>{fmt(computed.totals.recv)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: COLORS.textPrimary }}>{fmt(computed.totals.paid)}</td>
                  <td style={{ ...tdStyle, textAlign: "right", color: netColor(computed.totals.net) }}>
                    {computed.totals.net >= 0 ? "+" : ""}
                    {fmt(computed.totals.net)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

const miniInput = {
  background: COLORS.inputBg,
  color: COLORS.textPrimary,
  border: `1px solid ${COLORS.borderInput}`,
  borderRadius: 6,
};

const pillBtn = (theme, disabled) => ({
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  color: theme.color,
  fontWeight: 600,
  borderRadius: 6,
  opacity: disabled ? 0.45 : 1,
});

function GroupCard({ title, subtitle, color, fee, payout, live, footer }) {
  const net = fee - payout;
  return (
    <Card
      style={{
        background: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        borderTop: `2px solid ${color}`,
        marginBottom: 12,
        height: "calc(100% - 12px)", // 同一列三張等高（自營Bot 卡有時多一行「聯盟吃」）
      }}
    >
      <CardBody style={{ padding: "11px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{ width: 9, height: 9, borderRadius: "50%", background: color, display: "inline-block" }}
            />
            <span style={{ color: COLORS.textPrimary, fontWeight: 700, fontSize: 13.5 }}>{title}</span>
          </span>
          {live && (
            <Badge
              pill
              style={{
                background: "rgba(251,99,64,0.15)",
                border: `1px solid rgba(251,99,64,0.5)`,
                color: COLORS.warning,
                fontSize: 10,
                fontWeight: 500,
              }}
            >
              <i className="fa fa-bolt mr-1" />連動
            </Badge>
          )}
        </div>
        <div style={{ color: COLORS.textMuted, fontSize: 11, margin: "2px 0 8px 17px" }}>{subtitle}</div>

        <StatRow label="Fee" value={fee} sign="+" />
        <StatRow label="Payout" value={payout} sign="−" />

        <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "7px 0" }} />

        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <span style={{ color: COLORS.textSecondary, fontSize: 12, fontWeight: 600 }}>淨值</span>
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: netColor(net),
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {net >= 0 ? "+" : ""}
            {fmt(net)}
          </span>
        </div>
        {footer}
      </CardBody>
    </Card>
  );
}

/**
 * 自營Bot 卡片底部：同一筆錢在聯盟那邊的樣子。
 * Banker 收的 fee 是聯盟付的、Banker 付的 payout 是聯盟收的（營收「自營Bot 損益」），
 * 讓人看得出這筆錢沒有離開我們，只是從 Banker 移到聯盟（或反過來）。
 */
function UnionMirrorLine({ fee, payout }) {
  if (Math.abs(fee) < 0.5 && Math.abs(payout) < 0.5) return null;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        flexWrap: "wrap",
        gap: "2px 10px",
        marginTop: 7,
        paddingTop: 6,
        borderTop: "1px dashed rgba(255,255,255,0.1)",
        fontSize: 11.5,
        color: COLORS.textMuted,
      }}
    >
      <span>聯盟這邊（營收「自營Bot 損益」）</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        付 fee {fmt(fee)} · 收 payout {fmt(payout)}
      </span>
    </div>
  );
}

/** 週期明細表裡的 Eco Share% 輸入框。value 是 fraction，onChange 收 percent。 */
function ShareInput({ theme, value, onChange, onStep }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: 6,
        padding: "2px 4px",
      }}
    >
      <Input
        type="number"
        className="jpb-num"
        bsSize="sm"
        min={0}
        max={100}
        step={1}
        value={toPct(value)}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 36,
          textAlign: "right",
          background: "transparent",
          border: "none",
          color: theme.color,
          fontWeight: 700,
          padding: "2px 4px",
          boxShadow: "none",
        }}
      />
      <span style={{ color: theme.color, fontWeight: 700, fontSize: 12 }}>%</span>
      <Stepper color={theme.color} onUp={() => onStep(5)} onDown={() => onStep(-5)} />
    </div>
  );
}

function PoolValueCard({ pool }) {
  const has = pool && pool.value != null;
  const tsIso = has ? pool.captured_at || pool.received_at : null;
  const ageMin = tsIso ? (Date.now() - new Date(tsIso).getTime()) / 60000 : null;
  const stale = ageMin != null && ageMin > 60; // 逾 60 分鐘沒新值 → 提示可能過期
  return (
    <Card
      style={{
        background: COLORS.cardBg,
        border: `1px solid ${stale ? "rgba(251,99,64,0.45)" : COLORS.border}`,
        borderStyle: has ? "solid" : "dashed",
        marginBottom: 12,
        opacity: has ? 1 : 0.6,
      }}
    >
      <CardBody style={{ textAlign: "center", padding: "11px 14px" }}>
        <div style={{ color: COLORS.textMuted, fontSize: 11.5, marginBottom: 3 }}>獎池現值</div>
        {has ? (
          <>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary }}>
              {fmt(pool.value)}
            </div>
            <small style={{ color: stale ? COLORS.warning : COLORS.textMuted, fontSize: 10.5 }}>
              {stale && <i className="fa fa-exclamation-triangle mr-1" />}
              資料時間 {fmtTime(tsIso)}
              {stale ? "（可能過期）" : ""}
            </small>
          </>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 600, color: COLORS.textMuted }}>尚未取得</div>
            <small style={{ color: COLORS.textMuted }}>等待官網後臺推送</small>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * 「近 N 週」範圍控制：全部 / 近 4·8·13·26 週 快捷鈕 + 自訂 N。
 *
 * 🔑 跟 TinyWeekWindowNav 一樣用原生 <button>（不用 reactstrap Button）：
 *    主題的 `.btn` 有 hover 位移與陰影，看起來像按鈕在縮放。
 *    這裡三態只換顏色，高度鎖 WEEK_CTRL_HEIGHT 與其他頁的週期控制對齊。
 *
 * 自訂框清空 ＝ 回到全部 —— 否則會出現「沒有任何一顆亮著、數字卻是篩過的」。
 */
function RecentWeeksFilter({ value, onChange, span, count }) {
  const [custom, setCustom] = useState("");
  const customOn = custom !== "" && parseInt(custom, 10) === value;

  const pick = (n) => {
    setCustom("");
    onChange(n);
  };
  const typeCustom = (text) => {
    const clean = text.replace(/\D/g, "").slice(0, 3);
    setCustom(clean);
    const n = parseInt(clean, 10);
    onChange(n >= 1 ? n : null);
  };

  const H = WEEK_CTRL_HEIGHT;
  const on = { bg: "rgba(17,205,239,0.14)", border: "rgba(17,205,239,0.5)", color: THEME.navActive };
  const segBtn = (active) => ({
    height: H - 8, // 外框 border 1×2 + padding 3×2
    boxSizing: "border-box",
    margin: 0,
    padding: "0 11px",
    borderRadius: 6,
    border: `1px solid ${active ? on.border : "transparent"}`,
    background: active ? on.bg : "transparent",
    color: active ? on.color : COLORS.textSecondary,
    fontSize: 12.5,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "background .15s, color .15s, border-color .15s",
  });
  const options = [{ n: null, label: "全部" }, ...RECENT_PRESETS.map((n) => ({ n, label: `近 ${n} 週` }))];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            height: H,
            boxSizing: "border-box",
            padding: 3,
            background: COLORS.inputBg,
            border: `1px solid ${COLORS.borderInput}`,
            borderRadius: 8,
          }}
        >
          {options.map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => pick(o.n)}
              style={segBtn(!customOn && value === o.n)}
            >
              {o.label}
            </button>
          ))}
        </div>
        <label
          title="自訂週數，清空回到全部"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            height: H,
            boxSizing: "border-box",
            margin: 0,
            padding: "0 10px",
            background: customOn ? on.bg : COLORS.inputBg,
            border: `1px solid ${customOn ? on.border : COLORS.borderInput}`,
            borderRadius: 8,
            color: customOn ? on.color : COLORS.textMuted,
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "text",
            transition: "background .15s, color .15s, border-color .15s",
          }}
        >
          近
          <input
            type="text"
            inputMode="numeric"
            placeholder="N"
            value={custom}
            onChange={(e) => typeCustom(e.target.value)}
            style={{
              width: 30,
              background: "transparent",
              border: "none",
              outline: "none",
              padding: 0,
              textAlign: "center",
              color: customOn ? on.color : COLORS.textPrimary,
              fontSize: 13,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
            }}
          />
          週
        </label>
      </div>
      {span && (
        <small style={{ color: COLORS.textMuted, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
          {span} · 共 {count} 週
        </small>
      )}
    </div>
  );
}

function Stepper({ color, onUp, onDown }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", marginLeft: 2 }}>
      <button type="button" className="jpb-step" style={{ color }} onClick={onUp} tabIndex={-1} aria-label="加">
        <i className="fa fa-caret-up" />
      </button>
      <button type="button" className="jpb-step" style={{ color }} onClick={onDown} tabIndex={-1} aria-label="減">
        <i className="fa fa-caret-down" />
      </button>
    </span>
  );
}

function SecondaryStrip({ fee, payout }) {
  const net = fee - payout;
  const muted = "rgba(255,255,255,0.5)";
  const cell = (label, value, sign) => (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
      <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 12 }}>{label}</span>
      <span style={{ color: muted, fontSize: 14, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
        {sign && <span style={{ color: "rgba(255,255,255,0.25)", marginRight: 2 }}>{sign}</span>}
        {fmt(value)}
      </span>
    </span>
  );
  return (
    <Card
      style={{
        background: "rgba(255,255,255,0.02)",
        border: `1px dashed ${COLORS.border}`,
        marginBottom: 12,
      }}
    >
      <CardBody
        style={{ padding: "7px 14px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16 }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span
            style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(255,255,255,0.3)", display: "inline-block" }}
          />
          <span style={{ color: muted, fontWeight: 600, fontSize: 13 }}>Eco 原始</span>
          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>未套 Share · 次要參考</span>
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 26, flexWrap: "wrap" }}>
          {cell("Fee", fee, "+")}
          {cell("Payout", payout, "−")}
          {cell("淨值", net, net >= 0 ? "+" : "")}
        </span>
      </CardBody>
    </Card>
  );
}

function StatRow({ label, value, sign }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "1.5px 0" }}>
      <span style={{ color: COLORS.textMuted, fontSize: 11.5 }}>{label}</span>
      <span
        style={{
          color: COLORS.textPrimary,
          fontSize: 14.5,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {sign && (
          <span style={{ color: "rgba(255,255,255,0.3)", marginRight: 3, fontSize: 13 }}>{sign}</span>
        )}
        {fmt(value)}
      </span>
    </div>
  );
}

/* ⚠️ 這一整頁的摘要在 v2.35.0 統一縮過一輪 —— layout 不動（Brad 2026-09-10：
      「Layout 可以不用變，等於說全部一起縮小一點點」），只把 padding、
      字級與卡片間距按同一個比例收窄，讓主 table 進得了第一屏。
   🔑 改的時候**四張摘要卡要一起改**（SummaryCard / 累計淨損益 / PoolValueCard）——
      它們同一列並排，只縮一張會高度不齊。 */
function SummaryCard({ title, value, sub }) {
  return (
    <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}`, marginBottom: 12 }}>
      <CardBody style={{ textAlign: "center", padding: "11px 14px" }}>
        <div style={{ color: COLORS.textMuted, fontSize: 11.5, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 19, fontWeight: 700, color: COLORS.textPrimary }}>{fmt(value)}</div>
        <small style={{ color: COLORS.textMuted, fontSize: 10.5 }}>{sub}</small>
      </CardBody>
    </Card>
  );
}

export default TinyJPBanker;
